import type { PricingAgencyCommissionScope } from './adjustments';
import { z } from 'zod';
import {
  isPricingAdjustmentPercentInRange,
  PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
} from './adjustments';
import { resolvePricingLegacyCategoryKey } from './content-categories';

// XT-964 — the agency's commission, per category. Three sources set a rate: the
// organisation (XT › Organization settings), the agency (its CRM company) and
// the agent (their CRM contact). For each category the HIGHEST of them is the
// rate, so a deal can only raise what the organisation already pays.

export const COMMISSION_CATEGORIES = [
  'Accommodation',
  'Experiences',
  'Dining',
  'Transportation',
] as const;

export type CommissionCategory = (typeof COMMISSION_CATEGORIES)[number];

const commissionPercentSchema = z
  .number()
  .min(0, 'A commission cannot be negative')
  .max(100, 'A commission cannot exceed 100%')
  .multipleOf(0.01, 'Use at most two decimals');

/**
 * A rate per category; a missing category has no rate of its own. The shape of
 * the org setting and of a CRM contact's or agency's `commission_deals`.
 */
export const commissionDealsSchema = z
  .object({
    Accommodation: commissionPercentSchema.optional(),
    Experiences: commissionPercentSchema.optional(),
    Dining: commissionPercentSchema.optional(),
    Transportation: commissionPercentSchema.optional(),
  })
  .strict();

export type CommissionDeals = z.infer<typeof commissionDealsSchema>;

/** Reads a stored deal, dropping anything malformed rather than failing. */
export const parseCommissionDeals = (value: unknown): CommissionDeals => {
  if (!value || typeof value !== 'object') return {};
  const deals: CommissionDeals = {};
  for (const category of COMMISSION_CATEGORIES) {
    const parsed = commissionPercentSchema.safeParse(
      (value as Record<string, unknown>)[category]
    );
    if (parsed.success) deals[category] = parsed.data;
  }
  return deals;
};

/** Drops the categories left empty, so a cleared deal stores `{}`. */
export const compactCommissionDeals = (
  deals: Partial<Record<CommissionCategory, number | null | undefined>>
): CommissionDeals => {
  const out: CommissionDeals = {};
  for (const category of COMMISSION_CATEGORIES) {
    const value = deals[category];
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[category] = value;
    }
  }
  return out;
};

/**
 * The commission category of an item's content category. Activities and
 * attractions are Experiences; categories outside the four (a venue, personnel
 * on its own) have none and take the organisation's default.
 */
export const commissionCategoryOfContent = (
  contentCategory: string | null | undefined
): CommissionCategory | null => {
  switch (resolvePricingLegacyCategoryKey(contentCategory)) {
    case 'ACCOMMODATION':
      return 'Accommodation';
    case 'ACTIVITY':
    case 'ATTRACTION':
      return 'Experiences';
    case 'DINING':
      return 'Dining';
    case 'TRANSPORTATION':
      return 'Transportation';
    default:
      return null;
  }
};

/** The organisation's Agency Commission setting, as stored. */
export type OrgAgencyCommissionSetting = {
  default?: number;
  byCategory?: CommissionDeals | null;
} | null;

export type ResolvedCommissionRates = {
  byCategory: Record<CommissionCategory, number>;
  /** The rate for any category outside the four: the organisation's default. */
  other: number;
};

/**
 * For each category the highest of the organisation's rate (its category rate,
 * else its default) and every deal given (the agency's, the agent's).
 */
export const resolveCommissionRates = (input: {
  org: OrgAgencyCommissionSetting;
  deals?: readonly CommissionDeals[];
}): ResolvedCommissionRates => {
  // The org's default, else the product's 12% (not the first preset: a trip
  // with nothing configured keeps the rate the planner always showed).
  const configured = input.org?.default;
  const other =
    typeof configured === 'number' &&
    isPricingAdjustmentPercentInRange(configured)
      ? configured
      : PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT;
  const orgByCategory = parseCommissionDeals(input.org?.byCategory);
  const byCategory = {} as Record<CommissionCategory, number>;
  for (const category of COMMISSION_CATEGORIES) {
    let rate = orgByCategory[category] ?? other;
    for (const deal of input.deals ?? []) {
      const value = deal[category];
      if (typeof value === 'number' && value > rate) rate = value;
    }
    byCategory[category] = rate;
  }
  return { byCategory, other };
};

/** Rates when nothing is known: the product default for every category. */
export const DEFAULT_COMMISSION_RATES: ResolvedCommissionRates = {
  byCategory: {
    Accommodation: PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
    Experiences: PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
    Dining: PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
    Transportation: PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
  },
  other: PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
};

export const commissionRateFor = (
  rates: ResolvedCommissionRates,
  category: CommissionCategory | null
): number => (category ? rates.byCategory[category] : rates.other);

const CATEGORY_OF_SCOPE: Partial<
  Record<PricingAgencyCommissionScope, CommissionCategory>
> = {
  ACCOMMODATION: 'Accommodation',
  EXPERIENCES: 'Experiences',
  DINING: 'Dining',
  TRANSPORTATION: 'Transportation',
};

/**
 * The rate an Agency Commission line starts at for its scope. Services and
 * Other services span several categories, so they take the default rate.
 */
export const commissionRateForScope = (
  rates: ResolvedCommissionRates,
  scope: PricingAgencyCommissionScope
): number => commissionRateFor(rates, CATEGORY_OF_SCOPE[scope] ?? null);
